import { useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload, ExternalLink, Pin } from "lucide-react";

const empty = {
  name: "",
  category: "lingerie",
  price: "",
  short_description: "",
  description: "",
  image: "",
  tags: "",
  in_stock: true,
  inventory_count: "",
  supplier_name: "",
  supplier_url: "",
  supplier_sku: "",
  supplier_cost: "",
};

const CATEGORIES = ["lingerie", "toys", "accessories", "wellness", "games"];

/**
 * A cell that renders as plain text until clicked — then becomes a compact
 * inline input. Saves on blur or Enter, cancels on Escape. `onSave(newValue)`
 * gets a validated number back (nulls out if the field is blanked).
 */
const InlineNumberCell = ({ value, onSave, prefix, testId, disabled, color }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    let parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && Number.isNaN(parsed)) {
      setEditing(false);
      return;
    }
    if (parsed === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(parsed);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (disabled) {
    return (
      <span className="text-muted-bone" data-testid={testId}>
        {value == null ? "—" : `${prefix || ""}${Number(value).toFixed(prefix ? 2 : 0)}`}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => { setDraft(value == null ? "" : String(value)); setEditing(true); }}
        className="text-left hover:underline underline-offset-4 decoration-1 decoration-dotted"
        style={{ color: color || "inherit" }}
        title="Click to edit"
      >
        {value == null ? "—" : `${prefix || ""}${Number(value).toFixed(prefix ? 2 : 0)}`}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {prefix && <span className="text-muted-bone text-xs">{prefix}</span>}
      <input
        ref={inputRef}
        type="number"
        step={prefix ? "0.01" : "1"}
        min="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") setEditing(false);
        }}
        disabled={saving}
        className="w-20 bg-transparent border px-2 py-1 text-sm text-bone outline-none focus:border-white/40"
        style={{ borderColor: "rgba(212,175,55,0.5)" }}
      />
    </div>
  );
};

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  // Column filters — one entry per filterable column. Filter values are all
  // strings/blank; empty string means "no filter for this column".
  const [filters, setFilters] = useState({
    item: "",         // contains match on name or slug
    category: "",     // exact match or "" for all
    price_min: "",    // >= this dollar value
    stock: "",        // "in", "low", "out", "digital", or ""
    cost_min: "",     // >= this dollar value
    margin_min: "",   // >= this dollar value
    supplier: "",     // exact match on supplier_name; "__none__" = Not set
  });
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () =>
    setFilters({ item: "", category: "", price_min: "", stock: "", cost_min: "", margin_min: "", supplier: "" });
  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data || []));
  // Backfill job for "Redownload vendor images" — polled while running so
  // the admin can see live progress without leaving the page.
  const [backfill, setBackfill] = useState(null);
  const [backfillStarting, setBackfillStarting] = useState(false);
  useEffect(() => {
    load();
    // Prime the backfill status once on mount so a job started in another tab
    // still surfaces here.
    api.get("/admin/products/redownload-vendor-images/status")
      .then((r) => setBackfill(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (backfill?.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/products/redownload-vendor-images/status");
        setBackfill(data);
        if (data.status === "done" || data.status === "failed") {
          load();
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [backfill?.status]);

  const startBackfill = async () => {
    if (!window.confirm(
      "Walk every product that was imported before the new pipeline and re-download its real vendor image into storage?\n\nThis takes ~1–2 seconds per product. Runs in the background — you can leave this page and come back.",
    )) return;
    setBackfillStarting(true);
    try {
      const { data } = await api.post("/admin/products/redownload-vendor-images");
      if (data.queued === 0) {
        toast.success(data.message || "Every product already has a stored vendor image");
      } else if (data.already_running) {
        toast.info(`Backfill already running — ${data.processed || 0}/${data.queued}`);
      } else {
        toast.info(`Backfill started — ${data.queued} product(s) queued`);
      }
      // Kick a status refresh immediately so the badge shows up
      const { data: s } = await api.get("/admin/products/redownload-vendor-images/status");
      setBackfill(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed to start");
    } finally {
      setBackfillStarting(false);
    }
  };

  // Inline category override — changes the product's category immediately and
  // freezes it against the AI classifier's next reclassify run.
  const changeCategory = async (product, newCat) => {
    if (newCat === product.category) return;
    // Optimistic update so the dropdown feels instant
    setProducts((prev) =>
      prev.map((x) =>
        x.product_id === product.product_id
          ? { ...x, category: newCat, category_override: true }
          : x,
      ),
    );
    try {
      await api.patch(`/admin/products/${product.product_id}`, { category: newCat });
      toast.success(`Moved to ${newCat} — pinned against auto-reclassify`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Category update failed");
      load();  // roll back
    }
  };

  /**
   * Optimistically patch a numeric field on a product row and roll back if
   * the server rejects it. Used by the inline Price / Stock / Cost cells.
   */
  const patchField = async (product, field, newValue) => {
    const prevValue = product[field];
    setProducts((prev) =>
      prev.map((x) =>
        x.product_id === product.product_id ? { ...x, [field]: newValue } : x,
      ),
    );
    try {
      await api.patch(`/admin/products/${product.product_id}`, { [field]: newValue });
      toast.success(`${field.replace("_", " ")} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not save ${field}`);
      // Roll back the specific field so unrelated concurrent edits survive.
      setProducts((prev) =>
        prev.map((x) =>
          x.product_id === product.product_id ? { ...x, [field]: prevValue } : x,
        ),
      );
    }
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditor((prev) => ({ ...prev, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editor.name,
        category: editor.category,
        price: Number(editor.price),
        short_description: editor.short_description,
        description: editor.description,
        image: editor.image,
        images: [editor.image],
        tags: editor.tags
          ? editor.tags.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        in_stock: !!editor.in_stock,
        inventory_count:
          editor.inventory_count === "" || editor.inventory_count === null
            ? null
            : Math.max(0, Math.round(Number(editor.inventory_count))),
        supplier_name: editor.supplier_name || null,
        supplier_url: editor.supplier_url || null,
        supplier_sku: editor.supplier_sku || null,
        supplier_cost:
          editor.supplier_cost === "" || editor.supplier_cost === null
            ? null
            : Number(editor.supplier_cost),
      };
      if (editor.product_id) {
        await api.patch(`/admin/products/${editor.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setEditor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.product_id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="overline mb-3">Catalogue · Drop-ship</div>
          <h1 className="font-serif-display text-5xl text-bone">Products</h1>
          <p className="text-muted-bone mt-2 text-sm">{products.length} item{products.length !== 1 ? "s" : ""} — supplier-fulfilled · stock verified at checkout.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="admin-autotag-btn"
            onClick={async () => {
              const onlyUntagged = window.confirm(
                "Auto-tag ONLY products missing tags?\n\nOK  = only untagged\nCancel = ALL products (re-tag)"
              );
              try {
                const { data } = await api.post("/admin/products/auto-tag", {
                  limit: 200,
                  reset: false,
                  only_untagged: onlyUntagged,
                });
                toast.info(`Queued ${data.queued} of ${data.total_candidates || 0} products — refresh in ~30s`);
                setTimeout(load, 30000);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Auto-tag failed");
              }
            }}
            className="px-5 py-3 uppercase text-[11px] tracking-[0.22em] text-muted-bone border hover:text-bone hover:bg-white/5"
            style={{ borderColor: "var(--rs-border)" }}
          >
            Auto-tag products
          </button>
          <button
            data-testid="admin-reclassify-btn"
            onClick={async () => {
              if (!window.confirm("Re-run the category classifier on every imported product? Handcuffs will move to accessories, lube to wellness, etc.")) return;
              try {
                const { data } = await api.post("/admin/products/reclassify");
                toast.success(`Changed ${data.changed} · unchanged ${data.unchanged}`);
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Reclassify failed");
              }
            }}
            className="px-5 py-3 uppercase text-[11px] tracking-[0.22em] text-muted-bone border hover:text-bone hover:bg-white/5"
            style={{ borderColor: "var(--rs-border)" }}
          >
            Reclassify imports
          </button>
          <button
            data-testid="admin-regen-images-btn"
            onClick={async () => {
              try {
                const { data } = await api.get("/admin/products/missing-images");
                const count = data.count || 0;
                if (count === 0) {
                  toast.success("Every imported product already has a real photo");
                  return;
                }
                if (!window.confirm(`${count} products still use a category placeholder. Search vendor websites for the real product photo for ALL of them? Products where no vendor image can be found will keep their placeholder — no AI-generated imagery will be created.\n\nExpect this to take 1–2 seconds per product. Runs in the background.`)) return;
                // No batch cap — process every missing-image product in one go.
                const { data: r } = await api.post("/admin/products/regenerate-images", { limit: count, allow_ai_fallback: false });
                toast.info(`Queued ${r.queued} products · check back in a bit`);
                setTimeout(load, 60000);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Regenerate failed");
              }
            }}
            className="px-5 py-3 uppercase text-[11px] tracking-[0.22em] text-muted-bone border hover:text-bone hover:bg-white/5"
            style={{ borderColor: "var(--rs-border)" }}
          >
            Find real vendor images
          </button>
          <button
            data-testid="admin-backfill-images-btn"
            onClick={startBackfill}
            disabled={backfillStarting || backfill?.status === "running"}
            className="px-5 py-3 uppercase text-[11px] tracking-[0.22em] text-muted-bone border hover:text-bone hover:bg-white/5 disabled:opacity-50 flex items-center gap-2"
            style={{ borderColor: "var(--rs-border)" }}
            title="Walk every product imported before the new inline-image pipeline and re-download its real vendor photo into object storage."
          >
            {backfill?.status === "running" ? (
              <>
                <span
                  data-testid="admin-backfill-progress"
                  className="inline-flex items-center gap-2"
                >
                  <span
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ background: "#d4af37" }}
                  />
                  Backfilling {backfill.processed || 0}/{backfill.total || 0}
                  {backfill.updated ? ` · +${backfill.updated}` : ""}
                </span>
              </>
            ) : backfill?.status === "done" && backfill.updated ? (
              <span data-testid="admin-backfill-done">
                Redownloaded {backfill.updated} — run again
              </span>
            ) : (
              "Redownload vendor images"
            )}
          </button>
          <button
            data-testid="admin-product-new-btn"
            onClick={() => setEditor({ ...empty })}
            className="px-8 py-3 uppercase text-xs tracking-[0.22em] text-bone flex items-center gap-2"
            style={{ background: "var(--rs-oxblood)" }}
          >
            <Plus strokeWidth={1.5} size={14} /> New product
          </button>
        </div>
      </div>

      <div className="border" style={{ borderColor: "var(--rs-border)" }}>
        {(() => {
          // Compute unique supplier list from the current dataset — surfaces
          // "Not set" as a virtual value when at least one product lacks one.
          const supplierNames = Array.from(
            new Set(products.map((p) => p.supplier_name).filter(Boolean)),
          ).sort();
          const hasNoSupplier = products.some((p) => !p.supplier_name);

          // Apply all active filters. Kept inline (not memoised) — filtering a
          // few thousand rows is trivial and this keeps the code readable.
          const q = filters.item.trim().toLowerCase();
          const priceMin = filters.price_min ? parseFloat(filters.price_min) : null;
          const costMin = filters.cost_min ? parseFloat(filters.cost_min) : null;
          const marginMin = filters.margin_min ? parseFloat(filters.margin_min) : null;
          const filtered = products.filter((p) => {
            if (q && !(`${p.name} ${p.slug}`.toLowerCase().includes(q))) return false;
            if (filters.category && p.category !== filters.category) return false;
            if (priceMin !== null && !(Number(p.price) >= priceMin)) return false;
            if (filters.stock) {
              const isDigital = p.product_type === "ai_companion";
              const inv = p.inventory_count;
              if (filters.stock === "digital" && !isDigital) return false;
              if (filters.stock === "in" && (isDigital || inv == null || inv <= 0)) return false;
              if (filters.stock === "low" && (isDigital || inv == null || inv <= 0 || inv > 5)) return false;
              if (filters.stock === "out" && (isDigital || inv == null || inv > 0)) return false;
            }
            if (costMin !== null && !(Number(p.supplier_cost || 0) >= costMin)) return false;
            if (marginMin !== null) {
              const cost = Number(p.supplier_cost || 0);
              const margin = cost > 0 ? Number(p.price) - cost : null;
              if (margin === null || !(margin >= marginMin)) return false;
            }
            if (filters.supplier) {
              if (filters.supplier === "__none__") {
                if (p.supplier_name) return false;
              } else if (p.supplier_name !== filters.supplier) return false;
            }
            return true;
          });

          return (
            <>
              {hasActiveFilters && (
                <div
                  data-testid="admin-products-filter-summary"
                  className="flex items-center justify-between px-6 py-3 text-xs uppercase tracking-[0.22em]"
                  style={{ background: "rgba(60,10,15,0.35)", color: "var(--rs-terracotta)", borderBottom: "1px solid var(--rs-border)" }}
                >
                  <span>
                    Showing {filtered.length.toLocaleString()} of{" "}
                    {products.length.toLocaleString()}
                  </span>
                  <button
                    data-testid="admin-products-filter-clear"
                    onClick={clearFilters}
                    className="text-muted-bone hover:text-bone"
                  >
                    Clear filters ✕
                  </button>
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
                    <th className="text-left px-6 py-4">Item</th>
                    <th className="text-left px-6 py-4">Category</th>
                    <th className="text-left px-6 py-4">Price</th>
                    <th className="text-left px-6 py-4">Stock</th>
                    <th className="text-left px-6 py-4">Cost</th>
                    <th className="text-left px-6 py-4">Margin</th>
                    <th className="text-left px-6 py-4">Supplier</th>
                    <th className="text-right px-6 py-4">Actions</th>
                  </tr>
                  {/* Filter row — one input per column. Keeps parity with the
                      header above so cell alignment stays obvious. */}
                  <tr className="border-t" style={{ borderColor: "var(--rs-border)", background: "rgba(0,0,0,0.35)" }}>
                    <th className="px-6 py-3 font-normal">
                      <input
                        data-testid="admin-products-filter-item"
                        type="text"
                        value={filters.item}
                        onChange={(e) => setFilter("item", e.target.value)}
                        placeholder="Search name or slug…"
                        className="w-full bg-transparent border px-3 py-1.5 text-xs text-bone placeholder:text-muted-bone/70 outline-none focus:border-white/40"
                        style={{ borderColor: "var(--rs-border)" }}
                      />
                    </th>
                    <th className="px-6 py-3 font-normal">
                      <select
                        data-testid="admin-products-filter-category"
                        value={filters.category}
                        onChange={(e) => setFilter("category", e.target.value)}
                        className="w-full bg-transparent border px-2 py-1.5 text-xs uppercase tracking-widest text-muted-bone hover:text-bone focus:text-bone outline-none cursor-pointer"
                        style={{ borderColor: "var(--rs-border)" }}
                      >
                        <option value="" style={{ background: "var(--rs-bg)" }}>All</option>
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c} style={{ background: "var(--rs-bg)" }}>{c}</option>
                        ))}
                      </select>
                    </th>
                    <th className="px-6 py-3 font-normal">
                      <input
                        data-testid="admin-products-filter-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={filters.price_min}
                        onChange={(e) => setFilter("price_min", e.target.value)}
                        placeholder="≥ min"
                        className="w-full bg-transparent border px-3 py-1.5 text-xs text-bone placeholder:text-muted-bone/70 outline-none focus:border-white/40"
                        style={{ borderColor: "var(--rs-border)" }}
                      />
                    </th>
                    <th className="px-6 py-3 font-normal">
                      <select
                        data-testid="admin-products-filter-stock"
                        value={filters.stock}
                        onChange={(e) => setFilter("stock", e.target.value)}
                        className="w-full bg-transparent border px-2 py-1.5 text-xs uppercase tracking-widest text-muted-bone hover:text-bone focus:text-bone outline-none cursor-pointer"
                        style={{ borderColor: "var(--rs-border)" }}
                      >
                        <option value="" style={{ background: "var(--rs-bg)" }}>All</option>
                        <option value="in" style={{ background: "var(--rs-bg)" }}>In stock</option>
                        <option value="low" style={{ background: "var(--rs-bg)" }}>Low (≤5)</option>
                        <option value="out" style={{ background: "var(--rs-bg)" }}>Out</option>
                        <option value="digital" style={{ background: "var(--rs-bg)" }}>Digital</option>
                      </select>
                    </th>
                    <th className="px-6 py-3 font-normal">
                      <input
                        data-testid="admin-products-filter-cost"
                        type="number"
                        min="0"
                        step="0.01"
                        value={filters.cost_min}
                        onChange={(e) => setFilter("cost_min", e.target.value)}
                        placeholder="≥ min"
                        className="w-full bg-transparent border px-3 py-1.5 text-xs text-bone placeholder:text-muted-bone/70 outline-none focus:border-white/40"
                        style={{ borderColor: "var(--rs-border)" }}
                      />
                    </th>
                    <th className="px-6 py-3 font-normal">
                      <input
                        data-testid="admin-products-filter-margin"
                        type="number"
                        step="0.01"
                        value={filters.margin_min}
                        onChange={(e) => setFilter("margin_min", e.target.value)}
                        placeholder="≥ min"
                        className="w-full bg-transparent border px-3 py-1.5 text-xs text-bone placeholder:text-muted-bone/70 outline-none focus:border-white/40"
                        style={{ borderColor: "var(--rs-border)" }}
                      />
                    </th>
                    <th className="px-6 py-3 font-normal">
                      <select
                        data-testid="admin-products-filter-supplier"
                        value={filters.supplier}
                        onChange={(e) => setFilter("supplier", e.target.value)}
                        className="w-full bg-transparent border px-2 py-1.5 text-xs text-muted-bone hover:text-bone focus:text-bone outline-none cursor-pointer"
                        style={{ borderColor: "var(--rs-border)" }}
                      >
                        <option value="" style={{ background: "var(--rs-bg)" }}>All</option>
                        {supplierNames.map((s) => (
                          <option key={s} value={s} style={{ background: "var(--rs-bg)" }}>{s}</option>
                        ))}
                        {hasNoSupplier && (
                          <option value="__none__" style={{ background: "var(--rs-bg)" }}>— Not set —</option>
                        )}
                      </select>
                    </th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
              const cost = Number(p.supplier_cost || 0);
              const margin = cost > 0 ? p.price - cost : null;
              return (
                <tr
                  key={p.product_id}
                  data-testid={`admin-product-row-${p.slug}`}
                  className="border-t"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img src={resolveImg(p.image)} alt={p.name} className="w-12 h-16 object-cover" />
                      <div>
                        <div className="text-bone">{p.name}</div>
                        <div className="text-xs text-muted-bone">{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <select
                        data-testid={`admin-product-category-inline-${p.slug}`}
                        value={p.category}
                        onChange={(e) => changeCategory(p, e.target.value)}
                        className="bg-transparent border px-2 py-1.5 text-xs uppercase tracking-widest text-muted-bone hover:text-bone focus:text-bone outline-none cursor-pointer"
                        style={{ borderColor: "var(--rs-border)" }}
                        title={p.category_override ? "Category pinned — the classifier will leave it alone" : "Change to override the AI classifier"}
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c} style={{ background: "var(--rs-bg)" }}>
                            {c}
                          </option>
                        ))}
                      </select>
                      {p.category_override && (
                        <span
                          data-testid={`admin-product-category-pinned-${p.slug}`}
                          className="inline-flex items-center"
                          style={{ color: "var(--rs-terracotta)" }}
                          title="Pinned — the AI classifier will not overwrite this category"
                        >
                          <Pin strokeWidth={1.5} size={13} />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-bone" data-testid={`admin-product-price-${p.slug}`}>
                    <InlineNumberCell
                      value={p.price}
                      onSave={(v) => patchField(p, "price", v == null ? 0 : v)}
                      prefix="$"
                      testId={`admin-product-price-btn-${p.slug}`}
                      color="#f5f2eb"
                    />
                  </td>
                  <td
                    className="px-6 py-4"
                    data-testid={`admin-product-stock-${p.slug}`}
                    style={{
                      color:
                        p.product_type === "ai_companion" || p.inventory_count == null
                          ? "var(--rs-muted)"
                          : p.inventory_count <= 0
                            ? "#c05050"
                            : p.inventory_count <= 5
                              ? "#d4af37"
                              : "#f5f2eb",
                    }}
                  >
                    {p.product_type === "ai_companion" ? (
                      "∞ digital"
                    ) : (
                      <InlineNumberCell
                        value={p.inventory_count}
                        onSave={(v) => patchField(p, "inventory_count", v)}
                        testId={`admin-product-stock-btn-${p.slug}`}
                        color={
                          p.inventory_count == null
                            ? "var(--rs-muted)"
                            : p.inventory_count <= 0
                              ? "#c05050"
                              : p.inventory_count <= 5
                                ? "#d4af37"
                                : "#f5f2eb"
                        }
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 text-muted-bone" data-testid={`admin-product-cost-${p.slug}`}>
                    <InlineNumberCell
                      value={p.supplier_cost}
                      onSave={(v) => patchField(p, "supplier_cost", v)}
                      prefix="$"
                      testId={`admin-product-cost-btn-${p.slug}`}
                      color="var(--rs-muted)"
                    />
                  </td>
                  <td className="px-6 py-4" style={{ color: margin !== null ? "var(--rs-terracotta)" : "var(--rs-muted)" }}>
                    {margin !== null ? `$${margin.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-6 py-4 text-muted-bone text-xs">
                    {p.supplier_name ? (
                      <div className="flex items-center gap-1">
                        <span>{p.supplier_name}</span>
                        {p.supplier_url && (
                          <a
                            href={p.supplier_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-muted-bone hover:text-bone"
                            aria-label="supplier link"
                          >
                            <ExternalLink strokeWidth={1.5} size={12} />
                          </a>
                        )}
                      </div>
                    ) : (
                      "Not set"
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        data-testid={`admin-product-edit-${p.slug}`}
                        onClick={() => setEditor({ ...p, tags: (p.tags || []).join(", "), supplier_cost: p.supplier_cost ?? "" })}
                        className="text-muted-bone hover:text-bone"
                        aria-label="edit"
                      >
                        <Pencil strokeWidth={1.5} size={16} />
                      </button>
                      <button
                        data-testid={`admin-product-delete-${p.slug}`}
                        onClick={() => onDelete(p)}
                        className="text-muted-bone hover:text-bone"
                        aria-label="delete"
                      >
                        <Trash2 strokeWidth={1.5} size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {products.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-muted-bone">
                  No products yet. Add the first.
                </td>
              </tr>
            )}
            {products.length > 0 && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-muted-bone">
                  <div>Nothing matches these filters.</div>
                  <button
                    onClick={clearFilters}
                    className="mt-3 text-xs uppercase tracking-[0.22em]"
                    style={{ color: "var(--rs-terracotta)" }}
                  >
                    Clear filters
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
            </>
          );
        })()}
      </div>

      {editor && (
        <div
          data-testid="admin-product-editor"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10,10,10,0.92)" }}
        >
          <div
            className="w-full max-w-3xl border max-h-[90vh] overflow-y-auto"
            style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
          >
            <div
              className="flex items-center justify-between px-6 py-5 border-b"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <div>
                <div className="overline">{editor.product_id ? "Edit" : "New"} product</div>
                <div className="font-serif-display text-2xl text-bone">{editor.name || "Untitled"}</div>
              </div>
              <button
                data-testid="admin-product-editor-close"
                onClick={() => setEditor(null)}
                className="text-muted-bone hover:text-bone"
              >
                <X strokeWidth={1.5} size={18} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline block mb-2">Name</span>
                  <input
                    data-testid="admin-product-name-input"
                    required
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-2">Category</span>
                  <select
                    data-testid="admin-product-category-select"
                    value={editor.category}
                    onChange={(e) => setEditor({ ...editor, category: e.target.value })}
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="overline block mb-2">Retail price ($)</span>
                  <input
                    data-testid="admin-product-price-input"
                    required
                    type="number"
                    step="0.01"
                    min="0.5"
                    value={editor.price}
                    onChange={(e) => setEditor({ ...editor, price: e.target.value })}
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-2">Inventory count</span>
                  <input
                    data-testid="admin-product-inventory-input"
                    type="number"
                    step="1"
                    min="0"
                    value={editor.inventory_count ?? ""}
                    onChange={(e) => setEditor({ ...editor, inventory_count: e.target.value })}
                    placeholder="Units on hand — 0 blocks purchase"
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
                <label className="flex items-center gap-3 mt-7">
                  <input
                    data-testid="admin-product-instock-input"
                    type="checkbox"
                    checked={!!editor.in_stock}
                    onChange={(e) => setEditor({ ...editor, in_stock: e.target.checked })}
                  />
                  <span className="overline">Listed (available to buy)</span>
                </label>
              </div>

              {/* IMAGE */}
              <div>
                <span className="overline block mb-2">Image</span>
                <div className="flex items-start gap-4">
                  {editor.image ? (
                    <img
                      src={resolveImg(editor.image)}
                      alt="preview"
                      className="w-28 h-40 object-cover border"
                      style={{ borderColor: "var(--rs-border)" }}
                    />
                  ) : (
                    <div
                      className="w-28 h-40 border flex items-center justify-center text-muted-bone text-xs"
                      style={{ borderColor: "var(--rs-border)" }}
                    >
                      No image
                    </div>
                  )}
                  <div className="flex-1 space-y-3">
                    <button
                      type="button"
                      data-testid="admin-product-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="w-full px-4 py-3 text-xs uppercase tracking-[0.22em] text-bone border hover:bg-white/5 flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ borderColor: "rgba(245,242,235,0.25)" }}
                    >
                      <Upload strokeWidth={1.5} size={14} />
                      {uploading ? "Uploading…" : "Upload image"}
                    </button>
                    <input
                      ref={fileRef}
                      data-testid="admin-product-file-input"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleUpload}
                      className="hidden"
                    />
                    <input
                      data-testid="admin-product-image-input"
                      value={editor.image}
                      onChange={(e) => setEditor({ ...editor, image: e.target.value })}
                      placeholder="Or paste image URL"
                      className="w-full px-3 py-3 bg-transparent border text-bone outline-none text-xs"
                      style={{ borderColor: "var(--rs-border)" }}
                    />
                  </div>
                </div>
              </div>

              <label className="block">
                <span className="overline block mb-2">Short description</span>
                <input
                  data-testid="admin-product-short-input"
                  required
                  value={editor.short_description}
                  onChange={(e) => setEditor({ ...editor, short_description: e.target.value })}
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>
              <label className="block">
                <span className="overline block mb-2">Description</span>
                <textarea
                  data-testid="admin-product-desc-input"
                  required
                  rows={4}
                  value={editor.description}
                  onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none resize-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>
              <label className="block">
                <span className="overline block mb-2">Tags (comma-separated)</span>
                <input
                  data-testid="admin-product-tags-input"
                  value={editor.tags}
                  onChange={(e) => setEditor({ ...editor, tags: e.target.value })}
                  placeholder="silk, luxury"
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>

              {/* SUPPLIER */}
              <div
                className="border-t pt-5 mt-2"
                style={{ borderColor: "var(--rs-border)" }}
              >
                <div className="overline mb-1">Drop-ship supplier</div>
                <p className="text-xs text-muted-bone mb-4">
                  When an order is paid, you&apos;ll see these details so you can place the order on the supplier&apos;s site.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="overline block mb-2">Supplier name</span>
                    <input
                      data-testid="admin-product-supplier-name-input"
                      value={editor.supplier_name}
                      onChange={(e) => setEditor({ ...editor, supplier_name: e.target.value })}
                      placeholder="Spocket / CJ / AliExpress / direct"
                      className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                      style={{ borderColor: "var(--rs-border)" }}
                    />
                  </label>
                  <label className="block">
                    <span className="overline block mb-2">Supplier SKU</span>
                    <input
                      data-testid="admin-product-supplier-sku-input"
                      value={editor.supplier_sku}
                      onChange={(e) => setEditor({ ...editor, supplier_sku: e.target.value })}
                      placeholder="SKU-12345"
                      className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                      style={{ borderColor: "var(--rs-border)" }}
                    />
                  </label>
                </div>
                <label className="block mt-4">
                  <span className="overline block mb-2">Supplier product URL</span>
                  <input
                    data-testid="admin-product-supplier-url-input"
                    type="url"
                    value={editor.supplier_url}
                    onChange={(e) => setEditor({ ...editor, supplier_url: e.target.value })}
                    placeholder="https://supplier.com/products/…"
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
                <label className="block mt-4">
                  <span className="overline block mb-2">Supplier cost ($)</span>
                  <input
                    data-testid="admin-product-supplier-cost-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editor.supplier_cost}
                    onChange={(e) => setEditor({ ...editor, supplier_cost: e.target.value })}
                    placeholder="e.g. 18.50 (used to compute margin)"
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="px-6 py-3 text-xs uppercase tracking-[0.22em] text-muted-bone border"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  Cancel
                </button>
                <button
                  data-testid="admin-product-save-btn"
                  type="submit"
                  disabled={saving}
                  className="px-8 py-3 text-xs uppercase tracking-[0.22em] text-bone disabled:opacity-50"
                  style={{ background: "var(--rs-oxblood)" }}
                >
                  {saving ? "Saving…" : editor.product_id ? "Save changes" : "Create product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
